import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import {
  TECHNOLOGY_AREAS, TECHNOLOGY_AREA_LABEL_MAP,
  FUNDING_STATUS_OPTIONS, SEEKING_COLLABORATION_OPTIONS,
  ORG_LABELS, PARTICIPANT_TYPE_LABELS, DOD_ALIGNMENT_SUGGESTIONS,
  makeEmptyProject, projectDataToEntry,
} from '@/lib/constants';
import MilitaryFields from '@/components/registration/MilitaryFields';
import type { ParticipantType, ProjectEntry } from '@/lib/constants';
import type { Registration, OrganizationType, ProjectData } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TagInput } from '@/components/ui/tag-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, CheckCircle, AlertCircle, Plus, Trash2 } from 'lucide-react';

export default function RegisterUpdatePage() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [registration, setRegistration] = useState<Registration | null>(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [organizationType, setOrganizationType] = useState<OrganizationType | ''>('');
  const [participantType, setParticipantType] = useState<ParticipantType | ''>('');
  const [department, setDepartment] = useState('');
  const [rank, setRank] = useState('');
  const [branchOfService, setBranchOfService] = useState('');
  const [isRetired, setIsRetired] = useState(false);
  const [organization, setOrganization] = useState('');
  const [role, setRole] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [bio, setBio] = useState('');
  const [accelerationInterests, setAccelerationInterests] = useState<string[]>([]);
  const [projects, setProjects] = useState<ProjectEntry[]>([]);

  const isNpsStudentFaculty =
    organizationType === 'nps' &&
    (participantType === 'student' || participantType === 'faculty');

  useEffect(() => {
    if (!token) {
      setError('Invalid update link.');
      setLoading(false);
      return;
    }
    fetchRegistration();
  }, [token]);

  const fetchRegistration = async () => {
    try {
      const res = await api.get<{ success: boolean; data: Registration }>(`/register/${token}`);
      if (res.success) {
        const r = res.data;
        setRegistration(r);
        setFirstName(r.firstName || '');
        setLastName(r.lastName || '');
        setEmail(r.email || '');
        setPhone(r.phone || '');
        setOrganizationType(r.organizationType || '');
        setParticipantType((r.participantType as ParticipantType) || '');
        setDepartment(r.department || '');
        setRank(r.rank || '');
        setBranchOfService(r.branchOfService || '');
        setIsRetired(r.isRetired || false);
        setOrganization(r.organization || '');
        setRole(r.role || '');
        setLinkedinUrl(r.linkedinUrl || '');
        setWebsiteUrl(r.websiteUrl || '');
        setBio(r.bio || '');
        setAccelerationInterests(r.accelerationInterests || []);
        // Load projects — handle legacy single object and new array format
        if (r.projectData) {
          if (Array.isArray(r.projectData)) {
            setProjects(r.projectData.map(projectDataToEntry));
          } else {
            // Legacy single object
            setProjects([projectDataToEntry(r.projectData as unknown as ProjectData)]);
          }
        }
      }
    } catch {
      setError('This update link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  };

  const setProject = (index: number, field: keyof ProjectEntry, value: string | string[]) => {
    setProjects((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
    setFormErrors((prev) => ({ ...prev, [`p${index}_${field}`]: '' }));
  };

  const addProject = () => {
    if (projects.length >= 3) return;
    setProjects((prev) => [...prev, makeEmptyProject()]);
  };

  const removeProject = (index: number) => {
    setProjects((prev) => prev.filter((_, i) => i !== index));
    // Re-index error keys so remaining projects keep correct indices
    setFormErrors((prev) => {
      const next: Record<string, string> = {};
      for (const [key, val] of Object.entries(prev)) {
        const match = key.match(/^p(\d+)_(.+)$/);
        if (!match) { next[key] = val; continue; }
        const idx = parseInt(match[1]!, 10);
        if (idx === index) continue;
        const newIdx = idx > index ? idx - 1 : idx;
        next[`p${newIdx}_${match[2]!}`] = val;
      }
      return next;
    });
  };

  const toggleInterest = (interest: string) => {
    setAccelerationInterests((prev) =>
      prev.includes(interest)
        ? prev.filter((i) => i !== interest)
        : [...prev, interest]
    );
    setFormErrors((prev) => ({ ...prev, accelerationInterests: '' }));
  };

  const toggleProjectTechFocus = (index: number, value: string) => {
    setProjects((prev) =>
      prev.map((p, i) =>
        i === index
          ? { ...p, technologyFocus: p.technologyFocus.includes(value) ? p.technologyFocus.filter((v) => v !== value) : [...p.technologyFocus, value] }
          : p,
      ),
    );
    setFormErrors((prev) => ({ ...prev, [`p${index}_technologyFocus`]: '' }));
  };

  const toggleProjectSeeking = (index: number, value: string) => {
    setProjects((prev) =>
      prev.map((p, i) =>
        i === index
          ? { ...p, seeking: p.seeking.includes(value) ? p.seeking.filter((v) => v !== value) : [...p.seeking, value] }
          : p,
      ),
    );
    setFormErrors((prev) => ({ ...prev, [`p${index}_seeking`]: '' }));
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!firstName.trim()) errs.firstName = 'First name is required';
    if (!lastName.trim()) errs.lastName = 'Last name is required';
    if (!email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Invalid email';
    if (!participantType) errs.participantType = 'Participant type is required';
    if (!organizationType) errs.organizationType = 'Organization type is required';
    if (!bio.trim()) errs.bio = 'Bio is required';
    if (accelerationInterests.length === 0) errs.accelerationInterests = 'Select at least one technology interest';

    if (organizationType === 'government') {
      if (!organization.trim()) errs.organization = 'Organization is required';
      if (!branchOfService.trim()) errs.branchOfService = 'Branch of service is required';
    }
    if (organizationType === 'industry') {
      if (!organization.trim()) errs.organization = 'Organization is required';
      if (!role.trim()) errs.role = 'Position is required';
    }

    // Validate projects
    projects.forEach((p, i) => {
      const pfx = `p${i}_`;
      if (!p.title.trim()) errs[`${pfx}title`] = 'Title is required';
      if (!p.description.trim()) errs[`${pfx}description`] = 'Description is required';

      if (isNpsStudentFaculty) {
        if (!p.dodImpact.trim()) errs[`${pfx}dodImpact`] = 'Mission Impact is required';
        if (p.technologyFocus.length === 0) errs[`${pfx}technologyFocus`] = 'Select at least one technology focus';
        if (!p.fundingStatus) errs[`${pfx}fundingStatus`] = 'Funding status is required';
        if (p.seeking.length === 0) errs[`${pfx}seeking`] = 'Select at least one seeking option';
      } else {
        const cat = p.category;
        if (cat === 'research') {
          if (!p.department.trim()) errs[`${pfx}department`] = 'Department is required';
          if (!p.stage) errs[`${pfx}stage`] = 'Stage is required';
          if (!p.classification) errs[`${pfx}classification`] = 'Classification is required';
          if (p.researchAreas.length === 0) errs[`${pfx}researchAreas`] = 'At least one research area is required';
          if (p.keywords.length === 0) errs[`${pfx}keywords`] = 'At least one keyword is required';
          if (p.seeking.length === 0) errs[`${pfx}seeking`] = 'At least one seeking option is required';
          if (p.students.length === 0) errs[`${pfx}students`] = 'At least one team member is required';
        } else if (cat === 'collaboration') {
          if (p.researchAreas.length === 0) errs[`${pfx}researchAreas`] = 'At least one research area is required';
          if (p.keywords.length === 0) errs[`${pfx}keywords`] = 'At least one keyword is required';
          if (p.seeking.length === 0) errs[`${pfx}seeking`] = 'At least one seeking option is required';
          if (p.dodAlignment.length === 0) errs[`${pfx}dodAlignment`] = 'At least one DoD alignment is required';
        } else {
          if (!p.sponsorOrganization.trim()) errs[`${pfx}sponsorOrganization`] = 'Sponsor organization is required';
          if (!p.requirements.trim()) errs[`${pfx}requirements`] = 'Requirements are required';
          if (!p.benefits.trim()) errs[`${pfx}benefits`] = 'Benefits are required';
          if (!p.location.trim()) errs[`${pfx}location`] = 'Location is required';
          if (!p.duration.trim()) errs[`${pfx}duration`] = 'Duration is required';
          if (!p.deadline) errs[`${pfx}deadline`] = 'Deadline is required';
          if (p.dodAlignment.length === 0) errs[`${pfx}dodAlignment`] = 'At least one DoD alignment is required';
        }
        if (!p.pocFirstName.trim()) errs[`${pfx}pocFirstName`] = 'First name is required';
        if (!p.pocLastName.trim()) errs[`${pfx}pocLastName`] = 'Last name is required';
        if (!p.pocEmail.trim()) errs[`${pfx}pocEmail`] = 'Email is required';
        if (!p.pocRank.trim()) errs[`${pfx}pocRank`] = 'Rank/Title is required';
      }
    });

    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const buildProjectDataArray = () => {
    return projects.map((p) => {
      const pd: Record<string, unknown> = {
        title: p.title.trim(),
        description: p.description.trim(),
      };
      if (isNpsStudentFaculty) {
        pd.dodImpact = p.dodImpact.trim();
        pd.technologyFocus = p.technologyFocus;
        pd.fundingStatus = p.fundingStatus;
        pd.seeking = p.seeking;
      } else {
        const cat = p.category;
        if (cat === 'research') {
          pd.department = p.department.trim();
          pd.stage = p.stage;
          pd.classification = p.classification;
          pd.researchAreas = p.researchAreas;
          pd.keywords = p.keywords;
          pd.seeking = p.seeking;
          pd.students = p.students;
        } else if (cat === 'collaboration') {
          pd.researchAreas = p.researchAreas;
          pd.keywords = p.keywords;
          pd.seeking = p.seeking;
          pd.dodAlignment = p.dodAlignment;
        } else {
          pd.sponsorOrganization = p.sponsorOrganization.trim();
          pd.requirements = p.requirements.trim();
          pd.benefits = p.benefits.trim();
          pd.location = p.location.trim();
          pd.duration = p.duration.trim();
          pd.deadline = p.deadline;
          pd.dodAlignment = p.dodAlignment;
        }
        pd.pocFirstName = p.pocFirstName.trim();
        pd.pocLastName = p.pocLastName.trim();
        pd.pocEmail = p.pocEmail.trim();
        pd.pocRank = p.pocRank.trim();
      }
      return pd;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim() || null,
        organizationType: organizationType || undefined,
        participantType: participantType || null,
        department: department.trim() || null,
        rank: rank.trim() || null,
        branchOfService: branchOfService.trim() || null,
        isRetired,
        organization: organization.trim() || null,
        role: role.trim() || null,
        linkedinUrl: linkedinUrl.trim() || null,
        websiteUrl: websiteUrl.trim() || null,
        bio: bio.trim() || null,
        accelerationInterests,
      };
      body.projectData = projects.length > 0 ? buildProjectDataArray() : null;

      const res = await api.put<{ success: boolean }>(`/register/${token}`, body);
      if (res.success === false) {
        setFormErrors({ submit: 'Failed to update. Please try again.' });
        return;
      }
      setSubmitted(true);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setFormErrors({ submit: axiosErr?.response?.data?.error || 'Failed to update. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-3">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
            <CardTitle>Link Expired</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground">
              Contact <a href="mailto:converge@npsfoundation.org" className="text-primary hover:underline">converge@npsfoundation.org</a> for help.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-3">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-2" />
            <CardTitle>Registration Updated</CardTitle>
            <CardDescription>
              Your registration has been resubmitted for review. You'll receive an email once a decision has been made.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 md:py-12 px-4">
      <div className="mx-auto max-w-2xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Update Your Registration</h1>
          <p className="text-sm md:text-base text-muted-foreground mt-2">
            {registration?.event?.name ? `For ${registration.event.name}` : 'Update and resubmit for review'}
          </p>
          {registration?.adminMessage && (
            <div className="mt-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800">
              <p className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">Message from reviewer:</p>
              <p className="text-sm text-blue-700 dark:text-blue-300">{registration.adminMessage}</p>
            </div>
          )}
          {registration?.rejectionReason && (
            <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800">
              <p className="text-xs font-medium text-red-800 dark:text-red-200 mb-1">Previous rejection reason:</p>
              <p className="text-sm text-red-700 dark:text-red-300">{registration.rejectionReason}</p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Classification */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Classification</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm">Participant Type *</Label>
                <Select value={participantType} onValueChange={(v) => setParticipantType(v as ParticipantType)}>
                  <SelectTrigger className={formErrors.participantType ? 'border-destructive' : ''}>
                    <SelectValue placeholder="Select participant type" />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(PARTICIPANT_TYPE_LABELS) as ParticipantType[]).map((pt) => (
                      <SelectItem key={pt} value={pt}>{PARTICIPANT_TYPE_LABELS[pt]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formErrors.participantType && <p className="text-xs text-destructive">{formErrors.participantType}</p>}
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Organization Type *</Label>
                <Select value={organizationType} onValueChange={(v) => setOrganizationType(v as OrganizationType)}>
                  <SelectTrigger className={formErrors.organizationType ? 'border-destructive' : ''}>
                    <SelectValue placeholder="Select organization type" />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(ORG_LABELS) as OrganizationType[]).map((ot) => (
                      <SelectItem key={ot} value={ot}>{ORG_LABELS[ot]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formErrors.organizationType && <p className="text-xs text-destructive">{formErrors.organizationType}</p>}
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Personal Information</h2>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="First Name *" value={firstName} onChange={setFirstName} error={formErrors.firstName} />
                <Field label="Last Name *" value={lastName} onChange={setLastName} error={formErrors.lastName} />
              </div>
              <Field
                label="Email"
                value={email}
                type="email"
                disabled
                helperText="Email cannot be changed after initial registration. Contact converge@npsfoundation.org if you need to update it."
              />
              <Field label="Phone" value={phone} onChange={setPhone} type="tel" />
            </div>
          </div>

          {/* Organization Details */}
          {organizationType && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Organization Details</h2>
              <div className="space-y-3">
                {organizationType === 'nps' && (
                  <>
                    <Field label="Department" value={department} onChange={setDepartment} />
                    <MilitaryFields
                      branchOfService={branchOfService}
                      rank={rank}
                      isRetired={isRetired}
                      onBranchChange={(v) => { setBranchOfService(v); setRank(''); }}
                      onRankChange={setRank}
                      onRetiredChange={setIsRetired}
                    />
                  </>
                )}

                {organizationType === 'government' && (
                  <>
                    <Field label="Organization *" value={organization} onChange={setOrganization} error={formErrors.organization} />
                    <MilitaryFields
                      branchOfService={branchOfService}
                      rank={rank}
                      isRetired={isRetired}
                      onBranchChange={(v) => { setBranchOfService(v); setRank(''); setFormErrors((prev) => ({ ...prev, branchOfService: '' })); }}
                      onRankChange={setRank}
                      onRetiredChange={setIsRetired}
                      branchRequired
                      branchError={formErrors.branchOfService}
                    />
                    <Field label="Department" value={department} onChange={setDepartment} />
                  </>
                )}

                {organizationType === 'industry' && (
                  <>
                    <Field label="Organization *" value={organization} onChange={setOrganization} error={formErrors.organization} />
                    <Field label="Position *" value={role} onChange={setRole} error={formErrors.role} />
                    <Field label="Department" value={department} onChange={setDepartment} />
                  </>
                )}
              </div>
            </div>
          )}

          {/* Profile */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Profile</h2>
            <div className="space-y-3">
              <Field label="LinkedIn URL" value={linkedinUrl} onChange={setLinkedinUrl} type="url" />
              <Field label="Website URL" value={websiteUrl} onChange={setWebsiteUrl} type="url" />
              <div className="space-y-1.5">
                <Label className="text-sm">Bio *</Label>
                <Textarea
                  value={bio}
                  onChange={(e) => { setBio(e.target.value); setFormErrors((prev) => ({ ...prev, bio: '' })); }}
                  placeholder="Tell us about yourself and your work..."
                  rows={3}
                  maxLength={500}
                  className={`text-sm ${formErrors.bio ? 'border-destructive' : ''}`}
                />
                {formErrors.bio && <p className="text-xs text-destructive">{formErrors.bio}</p>}
                <p className="text-xs text-muted-foreground text-right">{bio.length}/500</p>
              </div>
            </div>
          </div>

          {/* Technology Interests */}
          <div className="space-y-3">
            <h2 className="text-lg font-semibold">Technology Interests *</h2>
            {formErrors.accelerationInterests && <p className="text-xs text-destructive">{formErrors.accelerationInterests}</p>}
            <div className="grid grid-cols-2 gap-2">
              {TECHNOLOGY_AREAS.map(({ value, label }) => (
                <label
                  key={value}
                  className={`flex items-center gap-2 p-2.5 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                    accelerationInterests.includes(value)
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <Checkbox
                    checked={accelerationInterests.includes(value)}
                    onCheckedChange={() => toggleInterest(value)}
                    className="h-4 w-4"
                  />
                  <span className="text-xs text-foreground flex-1">{label}</span>
                </label>
              ))}
            </div>
            {accelerationInterests.length > 0 && (
              <div className="pt-2">
                <p className="text-xs text-muted-foreground mb-1">Selected ({accelerationInterests.length}):</p>
                <div className="flex flex-wrap gap-1.5">
                  {accelerationInterests.map((interest) => (
                    <Badge key={interest} variant="secondary" className="text-xs px-2 py-0.5">
                      {TECHNOLOGY_AREA_LABEL_MAP[interest] || interest}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Projects */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Projects</h2>
              {projects.length < 3 && (
                <Button type="button" variant="outline" size="sm" onClick={addProject}>
                  <Plus className="mr-1 h-4 w-4" /> Add Project
                </Button>
              )}
            </div>

            {projects.length === 0 && (
              <p className="text-sm text-muted-foreground">No projects added. Click "Add Project" to include one.</p>
            )}

            {projects.map((proj, idx) => (
              <div key={idx} className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Project {idx + 1}</h3>
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeProject(idx)} className="text-destructive h-8 px-2">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                {/* Title & Description */}
                <PField label="Title *" field="title" index={idx} project={proj} errors={formErrors} setProject={setProject} />
                <div className="space-y-1.5">
                  <Label className="text-sm">Description *</Label>
                  <Textarea
                    value={proj.description}
                    onChange={(e) => setProject(idx, 'description', e.target.value)}
                    placeholder="Describe your project or opportunity..."
                    rows={4}
                    maxLength={5000}
                    className={`text-sm ${formErrors[`p${idx}_description`] ? 'border-destructive' : ''}`}
                  />
                  {formErrors[`p${idx}_description`] && <p className="text-xs text-destructive">{formErrors[`p${idx}_description`]}</p>}
                  <p className="text-xs text-muted-foreground text-right">{proj.description.length}/5000</p>
                </div>

                {/* Category-specific or NPS-specific fields */}
                {isNpsStudentFaculty ? (
                  <NpsProjectFields index={idx} project={proj} errors={formErrors} setProject={setProject} toggleTechFocus={toggleProjectTechFocus} toggleSeeking={toggleProjectSeeking} />
                ) : (
                  <>
                    <StandardProjectFields index={idx} project={proj} errors={formErrors} setProject={setProject} />
                    <div className="border-t pt-4 mt-4 space-y-3">
                      <Label className="text-sm font-medium">Point of Contact *</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <PField label="First Name *" field="pocFirstName" index={idx} project={proj} errors={formErrors} setProject={setProject} />
                        <PField label="Last Name *" field="pocLastName" index={idx} project={proj} errors={formErrors} setProject={setProject} />
                      </div>
                      <PField label="Email *" field="pocEmail" index={idx} project={proj} errors={formErrors} setProject={setProject} type="email" />
                      <PField label="Rank/Title *" field="pocRank" index={idx} project={proj} errors={formErrors} setProject={setProject} />
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {formErrors.submit && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{formErrors.submit}</div>
          )}

          <Button type="submit" className="w-full h-11 md:h-10" disabled={submitting}>
            {submitting ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating...</>
            ) : (
              'Update & Resubmit'
            )}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            Already have an account?{' '}
            <Link to="/auth" className="text-primary hover:underline">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

/** NPS student/faculty project fields */
function NpsProjectFields({
  index,
  project,
  errors,
  setProject,
  toggleTechFocus,
  toggleSeeking,
}: {
  index: number;
  project: ProjectEntry;
  errors: Record<string, string>;
  setProject: (index: number, field: keyof ProjectEntry, value: string | string[]) => void;
  toggleTechFocus: (index: number, value: string) => void;
  toggleSeeking: (index: number, value: string) => void;
}) {
  const pfx = `p${index}_`;
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label className="text-sm">Mission Impact *</Label>
        <Textarea
          value={project.dodImpact}
          onChange={(e) => setProject(index, 'dodImpact', e.target.value)}
          placeholder="Describe the mission impact of this project..."
          rows={3}
          maxLength={2000}
          className={`text-sm ${errors[`${pfx}dodImpact`] ? 'border-destructive' : ''}`}
        />
        {errors[`${pfx}dodImpact`] && <p className="text-xs text-destructive">{errors[`${pfx}dodImpact`]}</p>}
        <p className="text-xs text-muted-foreground text-right">{project.dodImpact.length}/2000</p>
      </div>

      <div className="space-y-1.5">
        <Label className="text-sm">Technology Focus *</Label>
        {errors[`${pfx}technologyFocus`] && <p className="text-xs text-destructive">{errors[`${pfx}technologyFocus`]}</p>}
        <div className="grid grid-cols-2 gap-2">
          {TECHNOLOGY_AREAS.map(({ value, label }) => (
            <label
              key={value}
              className={`flex items-center gap-2 p-2 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                project.technologyFocus.includes(value)
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <Checkbox
                checked={project.technologyFocus.includes(value)}
                onCheckedChange={() => toggleTechFocus(index, value)}
                className="h-4 w-4"
              />
              <span className="text-xs text-foreground flex-1">{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-sm">Funding Status *</Label>
        <Select value={project.fundingStatus} onValueChange={(v) => setProject(index, 'fundingStatus', v)}>
          <SelectTrigger className={errors[`${pfx}fundingStatus`] ? 'border-destructive' : ''}>
            <SelectValue placeholder="Select funding status" />
          </SelectTrigger>
          <SelectContent>
            {FUNDING_STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors[`${pfx}fundingStatus`] && <p className="text-xs text-destructive">{errors[`${pfx}fundingStatus`]}</p>}
      </div>

      <div className="space-y-1.5">
        <Label className="text-sm">Seeking *</Label>
        {errors[`${pfx}seeking`] && <p className="text-xs text-destructive">{errors[`${pfx}seeking`]}</p>}
        <div className="grid grid-cols-2 gap-2">
          {SEEKING_COLLABORATION_OPTIONS.map(({ value, label }) => (
            <label
              key={value}
              className={`flex items-center gap-2 p-2 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                project.seeking.includes(value)
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <Checkbox
                checked={project.seeking.includes(value)}
                onCheckedChange={() => toggleSeeking(index, value)}
                className="h-4 w-4"
              />
              <span className="text-xs text-foreground flex-1">{label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Standard (non-NPS) category-specific fields */
function StandardProjectFields({
  index,
  project,
  errors,
  setProject,
}: {
  index: number;
  project: ProjectEntry;
  errors: Record<string, string>;
  setProject: (index: number, field: keyof ProjectEntry, value: string | string[]) => void;
}) {
  const pfx = `p${index}_`;
  const cat = project.category;

  if (cat === 'research') {
    return (
      <div className="space-y-3">
        <PField label="Department *" field="department" index={index} project={project} errors={errors} setProject={setProject} />
        <div className="space-y-1.5">
          <Label className="text-sm">Stage *</Label>
          <Select value={project.stage} onValueChange={(v) => setProject(index, 'stage', v)}>
            <SelectTrigger className={errors[`${pfx}stage`] ? 'border-destructive' : ''}>
              <SelectValue placeholder="Select stage" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="concept">Concept</SelectItem>
              <SelectItem value="prototype">Prototype</SelectItem>
              <SelectItem value="pilot_ready">Pilot-Ready</SelectItem>
              <SelectItem value="deployed">Deployed</SelectItem>
            </SelectContent>
          </Select>
          {errors[`${pfx}stage`] && <p className="text-xs text-destructive">{errors[`${pfx}stage`]}</p>}
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">Classification *</Label>
          <Select value={project.classification} onValueChange={(v) => setProject(index, 'classification', v)}>
            <SelectTrigger className={errors[`${pfx}classification`] ? 'border-destructive' : ''}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Unclassified">Unclassified</SelectItem>
              <SelectItem value="CUI">CUI</SelectItem>
              <SelectItem value="Classified">Classified</SelectItem>
            </SelectContent>
          </Select>
          {errors[`${pfx}classification`] && <p className="text-xs text-destructive">{errors[`${pfx}classification`]}</p>}
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">Research Areas *</Label>
          <TagInput value={project.researchAreas} onChange={(v) => setProject(index, 'researchAreas', v)} placeholder="Add research areas..." />
          {errors[`${pfx}researchAreas`] && <p className="text-xs text-destructive">{errors[`${pfx}researchAreas`]}</p>}
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">Keywords *</Label>
          <TagInput value={project.keywords} onChange={(v) => setProject(index, 'keywords', v)} placeholder="Add keywords..." />
          {errors[`${pfx}keywords`] && <p className="text-xs text-destructive">{errors[`${pfx}keywords`]}</p>}
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">Seeking *</Label>
          <TagInput value={project.seeking} onChange={(v) => setProject(index, 'seeking', v)} placeholder="e.g., Funding, Partners, Students..." />
          {errors[`${pfx}seeking`] && <p className="text-xs text-destructive">{errors[`${pfx}seeking`]}</p>}
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">Students/Team Members *</Label>
          <TagInput value={project.students} onChange={(v) => setProject(index, 'students', v)} placeholder="Add team member names..." />
          {errors[`${pfx}students`] && <p className="text-xs text-destructive">{errors[`${pfx}students`]}</p>}
        </div>
      </div>
    );
  }

  if (cat === 'collaboration') {
    return (
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label className="text-sm">Research Areas *</Label>
          <TagInput value={project.researchAreas} onChange={(v) => setProject(index, 'researchAreas', v)} placeholder="Add research areas..." />
          {errors[`${pfx}researchAreas`] && <p className="text-xs text-destructive">{errors[`${pfx}researchAreas`]}</p>}
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">Keywords *</Label>
          <TagInput value={project.keywords} onChange={(v) => setProject(index, 'keywords', v)} placeholder="Add keywords..." />
          {errors[`${pfx}keywords`] && <p className="text-xs text-destructive">{errors[`${pfx}keywords`]}</p>}
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">Seeking *</Label>
          <TagInput value={project.seeking} onChange={(v) => setProject(index, 'seeking', v)} placeholder="e.g., Funding, Partners, Expertise..." />
          {errors[`${pfx}seeking`] && <p className="text-xs text-destructive">{errors[`${pfx}seeking`]}</p>}
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">DoD Alignment *</Label>
          <TagInput value={project.dodAlignment} onChange={(v) => setProject(index, 'dodAlignment', v)} placeholder="Add DoD alignment areas..." suggestions={DOD_ALIGNMENT_SUGGESTIONS} />
          {errors[`${pfx}dodAlignment`] && <p className="text-xs text-destructive">{errors[`${pfx}dodAlignment`]}</p>}
        </div>
      </div>
    );
  }

  // funding, internship, competition
  return (
    <div className="space-y-3">
      <PField label="Sponsor Organization *" field="sponsorOrganization" index={index} project={project} errors={errors} setProject={setProject} />
      <div className="space-y-1.5">
        <Label className="text-sm">Requirements *</Label>
        <Textarea
          value={project.requirements}
          onChange={(e) => setProject(index, 'requirements', e.target.value)}
          placeholder="What are the requirements?"
          rows={3}
          maxLength={2000}
          className={`text-sm ${errors[`${pfx}requirements`] ? 'border-destructive' : ''}`}
        />
        {errors[`${pfx}requirements`] && <p className="text-xs text-destructive">{errors[`${pfx}requirements`]}</p>}
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm">Benefits *</Label>
        <Textarea
          value={project.benefits}
          onChange={(e) => setProject(index, 'benefits', e.target.value)}
          placeholder="What benefits are offered?"
          rows={3}
          maxLength={2000}
          className={`text-sm ${errors[`${pfx}benefits`] ? 'border-destructive' : ''}`}
        />
        {errors[`${pfx}benefits`] && <p className="text-xs text-destructive">{errors[`${pfx}benefits`]}</p>}
      </div>
      <PField label="Location *" field="location" index={index} project={project} errors={errors} setProject={setProject} />
      <PField label="Duration *" field="duration" index={index} project={project} errors={errors} setProject={setProject} />
      <div className="space-y-1.5">
        <Label className="text-sm">Deadline *</Label>
        <Input
          type="date"
          value={project.deadline}
          onChange={(e) => setProject(index, 'deadline', e.target.value)}
          className={`h-11 md:h-10 text-sm ${errors[`${pfx}deadline`] ? 'border-destructive' : ''}`}
        />
        {errors[`${pfx}deadline`] && <p className="text-xs text-destructive">{errors[`${pfx}deadline`]}</p>}
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm">DoD Alignment *</Label>
        <TagInput value={project.dodAlignment} onChange={(v) => setProject(index, 'dodAlignment', v)} placeholder="Add DoD alignment areas..." suggestions={DOD_ALIGNMENT_SUGGESTIONS} />
        {errors[`${pfx}dodAlignment`] && <p className="text-xs text-destructive">{errors[`${pfx}dodAlignment`]}</p>}
      </div>
    </div>
  );
}

/** Project field input */
function PField({
  label, field, index, project, errors, setProject, type = 'text',
}: {
  label: string;
  field: keyof ProjectEntry;
  index: number;
  project: ProjectEntry;
  errors: Record<string, string>;
  setProject: (index: number, field: keyof ProjectEntry, value: string | string[]) => void;
  type?: string;
}) {
  const errKey = `p${index}_${field}`;
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <Input
        type={type}
        value={project[field] as string}
        onChange={(e) => setProject(index, field, e.target.value)}
        className={`h-11 md:h-10 text-sm ${errors[errKey] ? 'border-destructive' : ''}`}
      />
      {errors[errKey] && <p className="text-xs text-destructive">{errors[errKey]}</p>}
    </div>
  );
}

function Field({
  label, value, onChange, error, type = 'text', disabled = false, helperText,
}: {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  error?: string;
  type?: string;
  disabled?: boolean;
  helperText?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={disabled}
        className={`h-11 md:h-10 text-sm ${error ? 'border-destructive' : ''} ${disabled ? 'cursor-not-allowed opacity-70' : ''}`}
      />
      {helperText && <p className="text-xs text-muted-foreground">{helperText}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
